import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonViewWillEnter,
} from '@ionic/react'
import React, { BaseSyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { chevronBackOutline } from 'ionicons/icons'
import PCRHeader from '../../../../components/pcrHeader/PCRHeader.component'
import { getTitrateSelector } from '../selectors/titrate.selector'
import { ITitrateOptionsState } from '../../../options/store/options.types'
import { getTitrateOptionsSelector } from '../../../options/selectors/titrateOptions.selector'
import { ITitrateState } from '../store/titrate.types'
import {
  saveTitrate,
  setMedication,
  setNewDose,
  setOtherOption,
  setRate,
  setRatePNNV,
  setRoute,
  setRoutePNNV,
  setUpdateInputRows,
} from '../store/titrate.actions'
import { randomNumber } from '../../../../utilities/randomNumber.util'
import { TitrateConstants } from '../store/titrate.constants'
import { ProcedureConstants } from '../../../../components/procedure/store/procedure.constants'
import Procedure from '../../../../components/procedure/Procedure.component'
import { setInputRows } from '../../../../components/procedure/store/procedure.actions'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import { IInputRow } from '../../../../components/procedure/ProcedureDynamic.component'
import { setDeleteProcedure, setProcedureLoaderItem } from '../../../timeline/store/timeline.actions'
import { TimelineConstants } from '../../../timeline/store/timeline.constants'
import { filterOutObjectKeys, compareObjectsIfEqual } from '../../../../utilities/object.util'
import { showAlert } from '../../../vitals/store/vitals.actions'
import { initialTitrateState } from '../store/titrate.reducers'
import { IOption, IPNNVOption } from '../../../../common/common.types'
import { numberPadKeysWithDecimal } from '../../../../components/numberPad/NumberPad.types'
import { goBack } from '../../../../utilities/navigation.util'
import { IServiceConfig } from '../../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../../configurations/selectors/serviceConfig.selector'
import { IAlertState } from '../../../../components/alert/store/alert.types'
import { getCurrentDate, getCurrentTime } from '../../../../utilities'

const Titrate: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const titrate: ITitrateState = useSelector(getTitrateSelector)
  const titrateOptions: ITitrateOptionsState = useSelector(getTitrateOptionsSelector)
  const {
    date,
    time,
    assessedBy,
    pta,
    rolePerforming,
    rolePerformingPNNV,
    medication,
    otherOption,
    newDose,
    rate,
    ratePNNV,
    route,
    routePNNV,
    procedureComplications,
    response,
    responsePNNV,
    authorization,
    comments,
    updateInputRows,
    isEkgImport,
    procedurePerformedByAnotherUnit,
    procedurePerformedByAnotherUnitPNNV,
  } = titrate
  const { medicationOptions, rateOptions, routeOptions } = titrateOptions
  const serviceConfigData: IServiceConfig = useSelector(getServiceConfigSelector)
  const { DISPLAY_SECONDS } = serviceConfigData
  const displaySeconds = !!DISPLAY_SECONDS

  const medicationOnChange = (option: IOption) => {
    if (option) dispatch(setOtherOption(''))
    dispatch(setMedication(option))
  }

  const otherOptionOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setOtherOption(e.detail.value))
  }

  const newDoseOnChange = (values: string[]) => {
    dispatch(setNewDose(values.join('')))
  }

  const rateOnChange = (option: IOption) => {
    dispatch(setRate(option))
  }

  const ratePNNVOnChange = (pnnvOption: IPNNVOption | null) => {
    dispatch(setRatePNNV(pnnvOption))
    dispatch(setUpdateInputRows(true))
  }

  const routeOnChange = (option: IOption) => {
    dispatch(setRoute(option))
  }

  const routePNNVOnChange = (pnnvOption: IPNNVOption | null) => {
    dispatch(setRoutePNNV(pnnvOption))
    dispatch(setUpdateInputRows(true))
  }

  const dateTimeValidationCheck = () => {
    if (!date || !time) {
      dispatch(showAlert({ header: '', message: 'Date / Time is required' }))
      return false
    }
    return true
  }

  const initialStateCheck = () => {
    if (pta && ((date && time) || (!date && !time))) return true
    const fieldsToFilter = ['key', 'addToTimeline', 'updateInputRows']
    const currentState = filterOutObjectKeys(titrate, fieldsToFilter)
    const initalState = filterOutObjectKeys(initialTitrateState, fieldsToFilter)
    if (!compareObjectsIfEqual(currentState, initalState)) {
      return dateTimeValidationCheck()
    }
    return true
  }

  const onSaveClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (!pta && (!date || !time)) {
      dateTimeValidationCheck()
      return
    }
    const dateTimeValidationCheckResult = initialStateCheck()
    if (dateTimeValidationCheckResult) {
      history.push(TabConstants.TIMELINE_TAB)
    }
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if ((!date || !time) && !pta) {
      const alert: IAlertState = {
        message: t('common.saveTimelineEntry'),
        buttons: [
          {
            text: t('common.buttons.Yes'),
            handler: () => {
              const date = getCurrentDate()
              let time = getCurrentTime()
              if (!displaySeconds) {
                time = time.replace(/(:\d+)(:\d+)$/, '$1:00')
              }

              dispatch({ type: 'titrate/SET_TIME', time: time })
              dispatch({ type: 'titrate/SET_DATE', date: date })
              dispatch(saveTitrate())
              goBack()
            },
          },
          {
            text: t('common.buttons.No'),
            role: 'destructive',
            handler: () => {
              const itemToRemove = {
                key: titrate.key,
                rowActionType: TimelineConstants.SET_TITRATE_PROCEDURE_ITEM,
              }
              dispatch(setDeleteProcedure(true))
              dispatch({ type: TimelineConstants.DELETE_TIMELINE_ITEM, payload: itemToRemove })
              goBack()
            },
          },
        ],
      }
      dispatch(showAlert(alert))
      dispatch(setProcedureLoaderItem(false))
    } else {
      const dateTimeValidationCheck = initialStateCheck()
      if (dateTimeValidationCheck) {
        goBack()
      }
      dispatch(setProcedureLoaderItem(false))
    }
  }

  const inputRows: Array<IInputRow> = [
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: TitrateConstants.SET_MEDICATION,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('titrate.medication'),
            dataTestID: 'Medication',
            options: medicationOptions,
            action: medicationOnChange,
            listHeader: 'Medication',
            selectedOption: medication,
          },
        },
        {
          key: TitrateConstants.SET_OTHER_OPTION,
          type: ProcedureConstants.TEXT,
          hidden: false,
          props: {
            label: t('titrate.otherOption'),
            dataTestID: 'Other option',
            disabled: !!medication,
            inputValue: otherOption,
            onChangeAction: otherOptionOnChange,
          },
        },
      ],
    },

    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: TitrateConstants.SET_NEW_DOSE,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: false,
          props: {
            label: t('titrate.newDose'),
            dataTestId: 'New Dose',
            elementId: 'New Dose',
            values: newDose ? newDose.split('') : [],
            onNumPadClick: newDoseOnChange,
            validCharacters: numberPadKeysWithDecimal,
          },
        },
        {
          key: TitrateConstants.SET_RATE,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('titrate.rate'),
            dataTestID: 'Rate',
            options: rateOptions,
            action: rateOnChange,
            listHeader: 'Rate',
            selectedOption: rate,
            selectedPNNVOption: ratePNNV,
            pnnvAction: ratePNNVOnChange,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: TitrateConstants.SET_ROUTE,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('titrate.route'),
            dataTestID: 'Route',
            options: routeOptions,
            action: routeOnChange,
            listHeader: 'Route',
            selectedOption: route,
            selectedPNNVOption: routePNNV,
            pnnvAction: routePNNVOnChange,
          },
        },
      ],
    },
  ]

  useIonViewWillEnter(() => {
    dispatch(setUpdateInputRows(true))
    return
  })

  if (updateInputRows) {
    dispatch(setInputRows(inputRows))
    dispatch(setUpdateInputRows(false))
  }

  return (
    <IonPage id='TitratePage'>
      <PCRHeader />
      <IonHeader>
        <IonToolbar className='safe-area-space' color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' fill='clear' onClick={onBackClick} color='primary-alt'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton data-testid='Save' fill='clear' onClick={onSaveClick} color='primary-alt'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
          <IonTitle>Titrate</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen color='light'>
        <Procedure
          type={'titrate'}
          procedureDate={date}
          procedureTime={time}
          assessedBy={assessedBy}
          rolePerforming={rolePerforming}
          rolePerformingPNNV={rolePerformingPNNV}
          procedureComplications={procedureComplications}
          response={response}
          responsePNNV={responsePNNV}
          authorization={authorization}
          comments={comments}
          hideAttemptField={true}
          hideSuccessfulAttempt={true}
          pta={pta}
          isEkgImport={isEkgImport}
          procedurePerformedByAnotherUnitPNNV={procedurePerformedByAnotherUnitPNNV}
          procedurePerformedByAnotherUnit={procedurePerformedByAnotherUnit}
        />
      </IonContent>
    </IonPage>
  )
}

export default Titrate
